import { FC, useEffect, useState } from 'react';
import { Drawer, Box, Typography, Button, IconButton, Divider } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useSelector, RootState } from '../../store';
import { commentsGetAll, openCommentDialog, closeCommentDrawer } from '../../slices/commentSlice';
import { getActiveElement } from '../../utils/active-element';
import { useTranslation } from 'react-i18next';
import { CommentCard } from './comment-card';

// Right-side panel opened from a review page's "Comments" button. Shows the
// comment threads attached to the current page and creates new ones from an
// inline composer at the bottom (no modal).
export const CommentsDrawer: FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const { commentDrawerOpen, comments, commentStatus } = useSelector((s: RootState) => s.comment);
  const { parentType, parentId } = getActiveElement(location.pathname);
  // List privacy filter applied client-side to root-level comments.
  const [filter, setFilter] = useState<'all' | 'private' | 'shared'>('all');

  useEffect(() => {
    if (commentDrawerOpen) {
      dispatch(commentsGetAll() as any);
    }
  }, [commentDrawerOpen, dispatch]);

  // Refresh after a successful create/reply/edit while the drawer is open.
  useEffect(() => {
    if (commentDrawerOpen && commentStatus === 'success') {
      dispatch(commentsGetAll() as any);
    }
  }, [commentStatus]);

  const pageComments = (comments || []).filter(
    (c: any) =>
      c.parentType === parentType && (parentId ? String(c.parentId) === String(parentId) : true)
  );
  const topLevel = pageComments.filter((c: any) => !c.replyToId);
  // Client-side privacy filter on the root-level comments (Tous / Privés / Partagés).
  const visibleTopLevel = topLevel.filter((c: any) =>
    filter === 'all' ? true : filter === 'private' ? !c.isShared : !!c.isShared
  );

  return (
    <Drawer
      anchor="right"
      open={commentDrawerOpen}
      onClose={() => dispatch(closeCommentDrawer())}
      PaperProps={{ sx: { width: { xs: '100%', sm: 420 }, backgroundColor: 'background.default' } }}
    >
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {t('current_comments')}
          </Typography>
          <IconButton onClick={() => dispatch(closeCommentDrawer())} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Privacy filter for the root-level comments shown below. */}
        <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
          {([
            ['all', 'comment_filter_all'],
            ['private', 'comment_filter_private'],
            ['shared', 'comment_filter_shared'],
          ] as const).map(([key, label]) => (
            <Button
              key={key}
              size="small"
              variant={filter === key ? 'contained' : 'outlined'}
              onClick={() => setFilter(key)}
            >
              {t(label)}
            </Button>
          ))}
        </Box>

        <Box sx={{ overflowY: 'auto', flexGrow: 1 }}>
          {visibleTopLevel.length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              {t('no_comments')}
            </Typography>
          ) : (
            visibleTopLevel.map((c: any) => (
              <CommentCard
                key={c.id}
                comment={c}
                allComments={pageComments}
                showPrivateBadge={filter === 'all'}
              />
            ))
          )}
        </Box>

        {/* Open the full comment dialog to create a comment (replaces the old inline composer). */}
        <Divider sx={{ my: 1 }} />
        <Button
          variant="contained"
          fullWidth
          onClick={() => dispatch(openCommentDialog({ element: {}, isEditabled: true, isDuplicated: false }))}
        >
          {t('add_comment')}
        </Button>
      </Box>
    </Drawer>
  );
};
